import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { Outcome, OutcomeAtiBasis, OutcomeStatus } from '../outcomes.model';

const STATUS_OPTIONS: { label: string; value: OutcomeStatus }[] = [
  { label: 'Verified', value: 'verified' },
  { label: 'Unverified', value: 'unverified' },
];

const ATI_BASIS_OPTIONS: { label: string; value: OutcomeAtiBasis }[] = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Annual', value: 'annual' },
  { label: 'One-time', value: 'one_time' },
];

type OutcomeForm = {
  description: string;
  tags: string;
  dateAchieved: string;
  atiValue: number | null;
  atiBasis: OutcomeAtiBasis | null;
  linkedActionId: string;
  status: OutcomeStatus;
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

@Component({
  selector: 'bai-outcome-add-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, InputTextModule, InputNumberModule, SelectModule, TextareaModule],
  template: `
    <div class="flex flex-col gap-4 p-2">

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="oa-description">
          Outcome <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <textarea
          id="oa-description"
          pTextarea
          [ngModel]="form().description"
          (ngModelChange)="patch('description', $event)"
          placeholder="Describe the outcome"
          rows="3"
          class="w-full"
          aria-required="true"
        ></textarea>
        @if (submitted() && !form().description.trim()) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="oa-tags">
          Tags <span class="text-xs text-gray-400">(comma-separated)</span>
        </label>
        <input
          id="oa-tags"
          pInputText
          [ngModel]="form().tags"
          (ngModelChange)="patch('tags', $event)"
          placeholder="e.g. IRA, Tax, Savings"
          class="w-full"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="oa-date">
          Date Achieved
        </label>
        <input
          id="oa-date"
          pInputText
          type="date"
          [ngModel]="form().dateAchieved"
          (ngModelChange)="patch('dateAchieved', $event)"
          class="w-full"
        />
      </div>

      <div class="flex gap-3">
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-gray-700" for="oa-ati">
            ATI Value ($)
          </label>
          <p-inputnumber
            inputId="oa-ati"
            [ngModel]="form().atiValue"
            (ngModelChange)="patch('atiValue', $event)"
            [min]="0"
            [useGrouping]="true"
            placeholder="Optional"
            styleClass="w-full"
          />
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-gray-700" for="oa-basis">
            ATI Basis
            @if (form().atiValue !== null && form().atiValue! > 0) {
              <span class="text-red-500" aria-hidden="true">*</span>
            }
          </label>
          <p-select
            inputId="oa-basis"
            [options]="atiBasisOptions"
            [ngModel]="form().atiBasis"
            (ngModelChange)="patch('atiBasis', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select basis"
            styleClass="w-full"
          />
          @if (submitted() && atiBasisRequired()) {
            <span class="text-xs text-red-500" role="alert">Required when ATI value is set</span>
          }
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="oa-status">
          Status
        </label>
        <p-select
          inputId="oa-status"
          [options]="statusOptions"
          [ngModel]="form().status"
          (ngModelChange)="patch('status', $event)"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full"
        />
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <p-button
          label="Cancel"
          severity="secondary"
          [text]="true"
          (onClick)="cancel()"
        />
        <p-button
          label="Add Outcome"
          (onClick)="submit()"
        />
      </div>

    </div>
  `,
})
export class OutcomeAddDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);

  readonly statusOptions = STATUS_OPTIONS;
  readonly atiBasisOptions = ATI_BASIS_OPTIONS;

  readonly submitted = signal(false);

  readonly form = signal<OutcomeForm>({
    description: '',
    tags: '',
    dateAchieved: today(),
    atiValue: null,
    atiBasis: null,
    linkedActionId: '',
    status: 'verified',
  });

  readonly atiBasisRequired = computed(
    () => (this.form().atiValue ?? 0) > 0 && !this.form().atiBasis
  );

  patch<K extends keyof OutcomeForm>(key: K, value: OutcomeForm[K]): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  submit(): void {
    this.submitted.set(true);
    const f = this.form();
    if (!f.description.trim()) return;
    if ((f.atiValue ?? 0) > 0 && !f.atiBasis) return;

    const result: Omit<Outcome, 'id' | 'createdAt' | 'updatedAt'> = {
      clientId: '',
      description: f.description.trim(),
      tags: f.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      sourceType: 'human',
      status: f.status,
      dateAchieved: f.dateAchieved,
      atiValue: (f.atiValue ?? 0) > 0 ? f.atiValue : null,
      atiBasis: (f.atiValue ?? 0) > 0 ? f.atiBasis : null,
      linkedActionId: f.linkedActionId || null,
      timeToOutcomeDays: null,
      createdBy: 'FA',
      updatedBy: 'FA',
    };
    this._ref.close(result);
  }

  cancel(): void {
    this._ref.close(null);
  }
}
